<template>
  <div
    class="task-page-header d-flex justify-space-between align-center"
    data-testid="tasklist__header-action-group"
  >
    <div class="task-page-header__title-group d-flex flex-column" data-testid="tasklist__header-right-group">
      <div class="d-flex align-center">
        <h3 class="task-page-header__title ma-0" data-testid="tasklist__title">{{ title }}</h3>
        <v-tooltip bottom content-class="tooltip-bottom">
          <template v-slot:activator="{ on, attrs }">
            <base-button
              v-bind="attrs"
              v-on="on"
              icon="mdi-refresh"
              :icon-style="true"
              class="shadow-off"
              @click="$emit('refresh')"
              data-testid="tasklist__refresh-btn"
            />
          </template>
          <span>Refresh</span>
        </v-tooltip>
        <TasklistLoadProgress
          v-if="headerLoadProgress"
          class="ml-2"
          :loaded="headerLoadProgress.loaded"
          :total="headerLoadProgress.total"
          :status="headerLoadProgress.status"
          :error="headerLoadProgress.error || ''"
          :retrying="headerLoadProgress.retrying"
          @retry="$emit('retry-load')"
        />
      </div>
      <p class="task-page-header__description mb-0" data-testid="tasklist__subtitle">
        All reconciliation and checklist tasks assigned for the current close cycle.
      </p>
    </div>

    <div class="task-page-header__actions d-flex align-center" data-testid="tasklist__header-actions">
      <!-- <v-tooltip v-if="showNoa" bottom content-class="tooltip-bottom">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            icon
            class="shadow-off"
            @click="$emit('noa')"
            data-testid="tasklist__noa-btn"
          >
            <img
              src="/img/icons/fbc-icons/AskNoa.svg"
              :alt="$constants.ASK_NOA_LABEL"
              class="task-page-header__noa-img"
              data-testid="tasklist__noa-icon"
            />
          </v-btn>
        </template>
        <span>{{ $constants.ASK_NOA_LABEL }}</span>
      </v-tooltip> -->

      <slot name="actions" />

      <v-menu
        offset-y
        transition="slide-y-transition"
        :close-on-content-click="true"
        content-class="task-page-header__more-menu-panel"
        data-testid="tasklist__more-menu"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            icon
            class="shadow-off"
            aria-label="More actions"
            data-testid="tasklist__more-menu-btn"
          >
            <img src="/img/icons/threedot.svg" alt="More actions" class="task-page-header__more-menu-img rotate-90" />
          </v-btn>
        </template>
        <div class="task-page-header__more-menu" data-testid="tasklist__more-menu-list">
          <div class="task-page-header__more-menu-body">
            <button
              type="button"
              class="task-page-header__more-menu-item"
              :disabled="exportDisabled"
              :title="exportDisabled ? $options.TASKLIST_LOAD_MESSAGES.ACTIONS_LOCKED : null"
              @click.stop="!exportDisabled && $emit('export')"
              data-testid="tasklist__more-menu-export"
            >
              <span class="task-page-header__more-menu-item-content">
                <img src="/img/icons/fbc-icons/ExportIcon.svg" alt="" class="task-page-header__more-menu-img" />
                <span class="task-page-header__more-menu-label">Export</span>
              </span>
            </button>

            <button
              type="button"
              class="task-page-header__more-menu-item"
              @click.stop="openActivityLog"
              data-testid="tasklist__more-menu-activity-log"
            >
              <span class="task-page-header__more-menu-item-content">
                <img
                  src="/img/icons/activity-log.svg"
                  alt=""
                  class="task-page-header__more-menu-img"
                />
                <span class="task-page-header__more-menu-label">Activity Log</span>
              </span>
            </button>

            <button
              type="button"
              class="task-page-header__more-menu-item"
              @click.stop="$emit('status-report')"
              data-testid="tasklist__more-menu-status-report"
            >
              <span class="task-page-header__more-menu-item-content">
                <img
                  src="/img/icons/fbc-icons/AllTaskStatusReport.svg"
                  alt=""
                  class="task-page-header__more-menu-img"
                />
                <span class="task-page-header__more-menu-label">All Task Status Report</span>
              </span>
            </button>

          </div>
        </div>
      </v-menu>
      <activity-log
        hide-trigger
        ref="activityLog"
        :resource-id="metadata._id"
        row-description="Tasklist Bulk Actions"
        :type="normalizedActivityLogType"
        data-testid="financialclose-tasklist__activitylog-btn"
      />
    </div>
  </div>
</template>

<script>
import './TaskPageHeader.scss';
import ActivityLog from '../../../../../components/ActivityLog/ActivityLog.vue';
import TasklistLoadProgress from '../TasklistLoadProgress/TasklistLoadProgress.vue';
import { TASKLIST_LOAD_MESSAGES, TASKLIST_LOAD_STATUS } from '../../../../../utils/constants';

export default {
  name: 'TaskPageHeader',
  TASKLIST_LOAD_MESSAGES,
  components: {
    ActivityLog,
    TasklistLoadProgress,
  },
  props: {
    /** Progressive row load: { loaded, total, status, error, retrying }. null hides the progress. */
    loadProgress: {
      type: Object,
      default: null,
    },
    /** Export waits until every task row has loaded. */
    exportDisabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Tasks',
    },
    metadata: {
      type: Object,
      default: () => ({}),
    },
    showNoa: {
      type: Boolean,
      default: false,
    },
    activityLogType: {
      type: [Array, String],
      default: () => [],
    },
  },
  computed: {
    /** Progress beside Refresh. Hidden once loaded reaches total, unless Retry is showing. */
    headerLoadProgress() {
      let progress = null;
      const propOptions = this.$options.props;
      if (propOptions && Object.prototype.hasOwnProperty.call(propOptions, 'loadProgress')) {
        progress = this.loadProgress;
      } else {
        const attrs = this.$attrs || {};
        progress = attrs.loadProgress || attrs['load-progress'] || null;
      }
      if (!progress || typeof progress !== 'object') return null;
      const loaded = Number(progress.loaded) || 0;
      const total = Number(progress.total) || 0;
      const failed = progress.status === TASKLIST_LOAD_STATUS.ERROR;
      if (!failed && total > 0 && loaded >= total) return null;
      return progress;
    },
    normalizedActivityLogType() {
      if (Array.isArray(this.activityLogType)) {
        return this.activityLogType;
      }
      if (typeof this.activityLogType === 'string' && this.activityLogType.trim()) {
        return [this.activityLogType];
      }
      return [];
    },
  },
  methods: {
    openActivityLog() {
      const activityLog = this.$refs.activityLog;
      if (activityLog && typeof activityLog.openDialog === 'function') {
        activityLog.openDialog();
        return;
      }
      this.$nextTick(() => {
        this.$refs.activityLog?.openDialog();
      });
    },
  },
};
</script>
